import React, { useEffect, useState, useRef } from 'react';
import { Button, ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { Registerer, SessionState, UserAgent } from 'sip.js';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toZonedTime } from 'date-fns-tz';
import { startOfDay } from 'date-fns';
import * as Icon from 'react-feather';
import PropTypes from 'prop-types';

// COMPONENT IMPORTS
import config from '../../config';
import Timer from '../../utils/Timer';
import { socket } from '../../config/Socket';
import DtmfScreen from './dialScreens/DtmfScreen';
import DialPadScreen from './dialScreens/DialPadScreen';
import TransferScreen from './dialScreens/TransferScreen';
import ringtoneMp3 from '../../assets/sound/ringtone.mp3';
import { useCallingData } from '../Context/CallingContext';
import DefaultLiveCall from './dialScreens/DefaultLiveCall';
import ConferenceScreen from './dialScreens/ConferenceScreen';
import UserTransferScreen from './dialScreens/UserTransferScreen';
import IncomingCallScreen from './dialScreens/IncomingCallScreen';
import AgentTransferScreen from './dialScreens/AgentTransferScreen';
import ConferenceLiveScreen from './dialScreens/ConferenceLiveScreen';
import TransferHangupScreen from './dialScreens/TransferHangupScreen';
import { formatPayloadDate, getCurrentDateTime } from '../../utils/common';
import { toast } from 'react-toastify';

// APIS IMPORTS
import { setCallingModalOpen, setSidebarType, useTimezone } from '../../store/slices/commonSlice';
import { authLogout, useUserDetails } from '../../store/slices/authSlice';
import { clearPersister } from '../../utils/common';
import {
  onSetCallStatus,
  onSetCallType,
  setBreakDetails,
  setCallDialSec,
  setCallEnd,
  setCallId,
  setCallNumber,
  setCurrentCallDetail,
  setIsDialNumber,
  setIsReDial,
  setScreenType,
  setSelectBreakType,
  useBreakDetails,
  useBreakType,
  useCallId,
  useCallingStatus,
  useCurrentCallDetail,
  useScreenType,
  useIsDialNumber,
  useDialSec,
  useIsReDial,
  useCallEnd,
  useHoldTime,
  setHoldTime,
  setHoldStartTime,
  useHoldStartTime,
  setSipStatus,
  useCallType,
  useSipStatus,
  useConferenceDetail,
  setConferenceDetail
} from '../../store/slices/dialSlice';
import {
  breaksListGet,
  crmInfoLog,
  crmInformation,
  followUpAdd,
  handleConference,
  hangupCall,
  manualCall,
  manualDialing,
  pauseModeUpdate,
  progressiveCall,
  // removeCrmDetails,
  selectBreak,
  setIdleCall,
  setPauseMode,
  setUnregisterCall,
  updateCrmInformation,
  useBreaksList,
  useCrmDetails,
  usePauseMode
} from '../../store/slices/callingSlice';
import MicrophonePermissionModal from '../Modals/MicrophonePermissionModal';
import { playBeep } from '../../utils/audioUtils';

const CallingSidebar = ({ setIsProcessModalOpen, isMini = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const timezone = useTimezone();
  const today = toZonedTime(new Date(), timezone);
  const isEmbedded = window.self !== window.top;

  const {
    informationData,
    dispositionsData,
    fieldsData,
    followUp,
    isPauseMode,
    setFollowUp,
    setIsPauseMode,
    handleTimeReset,
    setMutedConference,
    setOnHoldConference,
    showMicModal,
    micPermission,
    requestMicrophonePermission
  } = useCallingData();
  const pauseMode = usePauseMode();
  const user = useUserDetails();
  const callStatus = useCallingStatus();
  const callId = useCallId();
  const breaksList = useBreaksList();
  const breakType = useBreakType();
  const screenType = useScreenType();
  const isDialNumber = useIsDialNumber();
  const breakDetails = useBreakDetails();
  const CurrentCallDetail = useCurrentCallDetail();
  const crmDetails = useCrmDetails();
  const dialSec = useDialSec();
  const isReDial = useIsReDial();
  const callEnd = useCallEnd();
  const holdTime = useHoldTime();
  const holdStartTime = useHoldStartTime();
  const callType = useCallType();
  const sipStatus = useSipStatus();
  const conferenceDetail = useConferenceDetail();

  const [isOpen, setIsOpen] = useState(false);
  const [isConVoxOpen, setIsConVoxOpen] = useState(false);
  const [number, setNumber] = useState('');
  const [duration, setDuration] = useState(0);
  const [wrapsec, setWrapsec] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const intervalRef = useRef(null);
  const incomingInvitationRef = useRef(null);
  const reconnectInProgressRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const ringtoneRef = useRef(null);
  const updateCrmRef = useRef();

  const onLogout = async () => {
    try {
      await dispatch(authLogout()).unwrap();
      socket.emit('user_logout', {
        user_id: user?.user_id,
        process_id: user?.process_details?.process_id,
        company_id: user?.companyId,
        type: user?.type
      });
      socket.disconnect();
      socket.off('user_logout', {
        user_id: user?.user_id,
        process_id: user?.process_details?.process_id,
        company_id: user?.companyId,
        type: user?.type
      });
      clearPersister(dispatch);
      window.__SIP_MANUAL_LOGOUT__ = true;
      localStorage.clear();
      localStorage.removeItem('timerDuration');
      localStorage.removeItem('timerStartTime');
      navigate('/login');
    } catch (error) {
      console.log('error', error);
    }
  };

  const userAgentRef = useRef(null);
  const registererRef = useRef(null);

  window.__SIP_MANUAL_LOGOUT__ = false;

  // const remoteStream = new MediaStream();

  const connectSIP = async () => {
    try {
      window.__SIP_MANUAL_LOGOUT__ = false;
      const sipUri = UserAgent.makeURI(
        `sip:${user.username}@${user.domain}:${process.env.REACT_APP_SIP_PORT}`
      );
      if (!sipUri) {
        console.error('Invalid SIP URI');
        return;
      }

      const userAgentOptions = {
        uri: sipUri,
        authorizationPassword: user?.password,
        authorizationUsername: user?.username,
        transportOptions: {
          server: config.WSS_URL,
          keepAliveInterval: 20,
          keepAliveDebounce: 10,
          connectionTimeout: 10
        },
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionConfiguration: {
            iceServers: [
              { urls: 'stun:stun.l.google.com:19302' },
              { urls: 'stun:stun1.l.google.com:19302' }
            ]
          }
        }
      };
      dispatch(setSipStatus('Connecting'));
      console.log('Creating UserAgent...');
      userAgentRef.current = new UserAgent(userAgentOptions);

      console.log('Starting UserAgent...');
      await userAgentRef.current.start();
      userAgentRef.current.transport.stateChange.addListener((state) => {
        if (state === 'Disconnected' && !window.__SIP_MANUAL_LOGOUT__) {
          reconnectUA();
        }
      });

      console.log('Registering UserAgent...');
      registererRef.current = new Registerer(userAgentRef.current);

      registererRef.current.stateChange.addListener((state) => {
        switch (state) {
          case 'Registered':
            console.log('✅ SIP Registered Successfully');
            dispatch(setSipStatus('Register'));
            break;

          case 'Unregistered':
            console.log('❌ SIP Unregistered');
            dispatch(setSipStatus('Un-Register'));
            break;

          case 'Terminated':
            console.log('⚠️ SIP Registration Terminated');
            dispatch(setSipStatus('Un-Register'));
            break;
        }
      });

      await registererRef.current.register();

      console.log('UserAgent registered and ready for calls.');
      userAgentRef.current.delegate = {
        onInvite: async (invitation) => {
          console.log('Incoming call...');
          incomingInvitationRef.current = invitation;
          const userProcessType = user?.process_details?.type;
          if (
            process.env.REACT_APP_INCOMING_SCREEN === 'true' &&
            (userProcessType === 'InboundAndManual' ||
              userProcessType === 'BlendedAndPredictive' ||
              userProcessType === 'BlendedAndPreview' ||
              userProcessType === 'BlendedAndProgressive')
          ) {
            playRingtone();

            invitation.stateChange.addListener((state) => {
              if (state === SessionState.Terminated) {
                stopRingtone();
                incomingInvitationRef.current = null;
              }
            });
          } else {
            try {
              await invitation.accept({
                sessionDescriptionHandlerOptions: {
                  constraints: {
                    audio: true,
                    video: false
                  }
                }
              });
              setupRemoteMedia(invitation);
              console.log('Call answered!');
            } catch (err) {
              console.error('Error answering call:', err);
            }
          }
        }
      };
    } catch (error) {
      console.error('SIP Connection Error:', error);
    }
  };

  const answerCall = async () => {
    const invitation = incomingInvitationRef.current;
    if (!invitation) return;
    stopRingtone();

    try {
      await invitation.accept({
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: true,
            video: false
          }
        }
      });
      setupRemoteMedia(invitation);
      console.log('Incoming call answered!');
      incomingInvitationRef.current = null;
    } catch (err) {
      incomingInvitationRef.current = null;
      console.error('Error answering Incoming call:', err);
    }
  };

  const registerUA = async () => {
    if (!userAgentRef.current) return;

    try {
      if (!registererRef.current || registererRef.current.state === 'Terminated') {
        registererRef.current = new Registerer(userAgentRef.current);
        registererRef.current.stateChange.addListener((state) => {
          switch (state) {
            case 'Registered':
              console.log('✅ SIP Registered Successfully');
              dispatch(setSipStatus('Register'));
              break;
            case 'Unregistered':
              console.log('❌ SIP Unregistered');
              dispatch(setSipStatus('Un-Register'));
              break;
            case 'Terminated':
              console.log('⚠️ SIP Registration Terminated');
              dispatch(setSipStatus('Un-Register'));
              break;
          }
        });
      }

      registererRef.current
        .register()
        .then(async () => {
          console.log('UA Register request sent ...');
        })
        .catch(async (error) => {
          console.error('Failed to send REGISTER ==> ', error);
        });
    } catch (error) {
      console.error('CATCH FOUND -  registerUA ==> ', error);
    }
  };

  const reconnectUA = async () => {
    if (reconnectInProgressRef.current || window.__SIP_MANUAL_LOGOUT__ || !userAgentRef.current) {
      return false;
    }
    reconnectInProgressRef.current = true;
    try {
      console.log('Connecting------------->');
      await userAgentRef.current.reconnect();
      dispatch(setSipStatus('Connecting'));
      await registerUA();
      // Connected again — clear any pending retry and reset the backoff.
      reconnectAttemptsRef.current = 0;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    } catch (error) {
      console.error('UA Reconnection error ==> ', error);
      // A single attempt isn't enough: when the machine is idle/asleep the network
      // can stay suspended far longer than one reconnect call. Keep retrying with a
      // capped exponential backoff so the phone re-registers once the network is back.
      if (!window.__SIP_MANUAL_LOGOUT__ && userAgentRef.current) {
        const attempt = reconnectAttemptsRef.current;
        const delay = Math.min(30000, 2000 * 2 ** attempt);
        reconnectAttemptsRef.current = attempt + 1;
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null;
          reconnectUA();
        }, delay);
      }
    } finally {
      reconnectInProgressRef.current = false;
    }
  };

  // const setupRemoteMedia = (mediaSession) => {
  //   const mediaElement = document.getElementById('mediaElement');
  //   try {
  //     mediaSession.sessionDescriptionHandler.peerConnection.getReceivers().forEach((receiver) => {
  //       if (receiver.track) {
  //         remoteStream.addTrack(receiver.track);
  //         mediaElement.srcObject = remoteStream;
  //         mediaElement.play();
  //       }
  //     });
  //   } catch (error) {
  //     console.log('Media audio session error - ', error);
  //   }
  // };

  const setupRemoteMedia = (mediaSession) => {
    const mediaElement = document.getElementById('mediaElement');
    if (!mediaElement) return;

    try {
      mediaElement.pause();
      mediaElement.srcObject = null;

      const stream = new MediaStream();

      mediaSession.sessionDescriptionHandler.peerConnection.getReceivers().forEach((receiver) => {
        if (receiver.track && receiver.track.kind === 'audio') {
          stream.addTrack(receiver.track);
        }
      });

      mediaElement.srcObject = stream;

      mediaElement.play().catch(() => {
        console.warn('Audio play interrupted or blocked by browser');
      });
    } catch (error) {
      console.log('Media audio session error - ', error);
    }
  };

  const playRingtone = () => {
    if (!ringtoneRef.current) return;
    if (!ringtoneRef.current.paused) return;

    ringtoneRef.current.play().catch(() => console.warn('Ringtone blocked'));
  };

  const stopRingtone = () => {
    if (!ringtoneRef.current) return;
    ringtoneRef.current.pause();
    ringtoneRef.current.currentTime = 0;
  };

  useEffect(() => {
    if (isMini) return;
    ringtoneRef.current = new Audio(ringtoneMp3);
    ringtoneRef.current.loop = true;

    return () => {
      ringtoneRef.current?.pause();
      ringtoneRef.current = null;
    };
  }, [isMini]);

  useEffect(() => {
    if (isMini) return;
    const handleOnline = async () => {
      if (Number(user.web_phone) === 0) {
        reconnectUA();
      }
    };

    const handleOffline = () => {
      if (Number(user.web_phone) === 0) {
        dispatch(setSipStatus('Un-Register'));
        alert('Please check your connection');
      }
    };

    // When the tab/machine resumes from sleep, Chrome often does NOT fire `online`,
    // so we also re-check on visibility. Only reconnect when the transport is not
    // already connected — never disrupt a healthy registration.
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (Number(user.web_phone) !== 0 || breakDetails) return;
      const transportState = userAgentRef.current?.transport?.state;
      if (transportState && transportState !== 'Connected') {
        reconnectAttemptsRef.current = 0;
        reconnectUA();
      }
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user.web_phone, isMini, breakDetails]);


  useEffect(() => {
    if (isMini) return;
    if (Number(user.web_phone) === 0 && !breakDetails) {
      reconnectInProgressRef.current = false;
      reconnectAttemptsRef.current = 0;
      Object.defineProperty(window, 'isSecureContext', { get: () => true });

      if (!userAgentRef.current) {
        connectSIP();
      } else {
        reconnectUA();
      }
    } else {
      reconnectInProgressRef.current = true;
      // Cancel any pending reconnect retry while SIP is intentionally disabled (break / web_phone off).
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      dispatch(setSipStatus('Un-Register'));
      console.log('SIP registration is disabled due to web_phone flag.');
    }

    return () => {    
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (userAgentRef.current) {
        dispatch(setSipStatus('Un-Register'));
        console.log('Stopping UserAgent...');
        userAgentRef.current.stop();
        userAgentRef.current = null;
      }
    };
  }, [user.web_phone, breakDetails, isMini]);

  useEffect(() => {
    let timerInterval;
    if (callStatus === 'WRAPUP') {
      timerInterval = setInterval(() => {
        setWrapsec((prevTime) => prevTime + 1);
      }, 1000);
    }
    return () => {
      clearInterval(timerInterval);
    };
  }, [callStatus]);

  useEffect(() => {
    // const autoWrapUpZero = String(user?.process_details?.autoWrapup) === '0';
    const autoWrapUp = Number(user?.process_details?.autoWrapup) || 10;
    // const isAuto = autoWrapUpZero && wrapsec > autoWrapUp;
    const isAuto = wrapsec > autoWrapUp;
    // In an iframe the host app owns the CRM update — never run updateCrm here.
    if (
      (isAuto || callEnd) &&
      !isLoading &&
      location.pathname !== '/crm' &&
      dialSec !== null &&
      !isEmbedded
    ) {
      updateCrmRef.current(isAuto);
    }
  }, [wrapsec, location.pathname, callEnd]);

  useEffect(() => {
    const autoWrapUp = Number(user?.process_details?.autoWrapup) || 10;
    const dispoRequired = user?.process_details?.is_dispo_required === 'yes';
    const subDispoRequired = user?.process_details?.is_sub_dispo_required === 'yes';
    const subSubDispoRequired = user?.process_details?.is_sub_sub_dispo_required === 'yes';

    if (callStatus === 'WRAPUP' && autoWrapUp > 10 && location.pathname !== '/crm') {
      if (autoWrapUp - wrapsec === 5) {
        const needsDispo = dispoRequired && !dispositionsData?.dispositioncode;
        const needsSubDispo = subDispoRequired && !dispositionsData?.subdispositioncode;
        const needsSubSubDispo = subSubDispoRequired && !dispositionsData?.subsubdispositioncode;

        if (needsDispo || needsSubDispo || needsSubSubDispo) {
          toast.warning('Please fill the required disposition fields. 5 seconds remaining.');
        }
      }
    }
  }, [wrapsec, callStatus, location.pathname]);

  const onPauseModeChange = async (pause_mode) => {
    try {
      const payload = { user_id: user?.id, pause_mode: pause_mode };
      const response = await dispatch(pauseModeUpdate(payload)).unwrap();
      if (response && response?.success) {
        dispatch(setPauseMode(payload?.pause_mode));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleConferenceMode = async () => {
    try {
      const payload = {
        conf_id: 'all',
        type: 'hangup',
        conf_name: conferenceDetail?.conference_name
      };
      await dispatch(handleConference(payload)).unwrap();
      dispatch(setConferenceDetail(null));
      setMutedConference([]);
      setOnHoldConference([]);
    } catch (error) {
      console.log('error', error);
    }
  };

  const updateCrm = async (isAuto = false) => {
    setIsLoading(true);
    const dispoRequired = user?.process_details?.is_dispo_required === 'yes';
    const subDispoRequired = user?.process_details?.is_sub_dispo_required === 'yes';
    const subSubDispoRequired = user?.process_details?.is_sub_sub_dispo_required === 'yes';

    if (!isAuto && !isReDial) {
      if (dispoRequired && !dispositionsData?.dispositioncode) {
        toast.error('Please select a disposition');
        setIsLoading(false);
        dispatch(setCallEnd(false));
        return;
      }
      if (subDispoRequired && !dispositionsData?.subdispositioncode) {
        toast.error('Please select a sub disposition');
        setIsLoading(false);
        dispatch(setCallEnd(false));
        return;
      }
      if (subSubDispoRequired && !dispositionsData?.subsubdispositioncode) {
        toast.error('Please select a sub-sub disposition');
        setIsLoading(false);
        dispatch(setCallEnd(false));
        return;
      }
    }

    if (conferenceDetail && conferenceDetail?.details?.length > 0) {
      handleConferenceMode();
    }
    try {
      // const autoWrapUpZero = String(user?.process_details?.autoWrapup) === '0';
      const autoWrapup = Number(user?.process_details?.autoWrapup) || 10;

      let hold_time = 0;

      if (holdStartTime !== null) {
        const holdDuration = Math.floor((Date.now() - holdStartTime) / 1000);
        hold_time = holdTime + holdDuration;
      } else {
        hold_time = holdTime;
      }

      const payload = {
        data: [
          {
            crmDetails: { ...informationData }
          },
          {
            fields: fieldsData?.fields ? fieldsData?.fields : []
          },
          {
            dispositions: dispositionsData?.dispositioncode
              ? { ...dispositionsData }
              : { dispositioncode: 'AUTOWRAP' }
          },
          {
            unique_id: crmDetails?.[4]?.unique_id,
            dialsec: callType === 'predictive' || callType === 'incoming' ? 0 : dialSec,
            hold_time: hold_time,
            wrapsec: wrapsec,
            // auto_wrapup: !autoWrapUpZero ? autoWrapup <= wrapsec : false
            auto_wrapup: autoWrapup <= wrapsec
          }
        ]
      };
      if (followUp?.isFollowUp === 'yes') {
        dispatch(
          followUpAdd({
            phone_number: crmDetails?.[1]?.crmDetails?.primaryNumber,
            crm_id: crmDetails?.[1]?.crmDetails?.Crms_id,
            date_and_time: formatPayloadDate(followUp?.date_and_time)
          })
        ).unwrap();
      }
      if (isPauseMode) {
        onPauseModeChange(1);
      }
      const data_message = {
        user_id: user.user_id,
        payload: payload,
        message: 'update Crm Information'
      };
      getLogDetails(data_message);
      const response = await dispatch(updateCrmInformation(payload)).unwrap();
      if (response) {
        const responseTimer = setTimeout(() => {
          if (isReDial) {
            dispatch(setIsDialNumber(crmDetails?.[1]?.crmDetails?.primaryNumber));
          }
          setWrapsec(0);
          dispatch(setCallId(null));
          dispatch(setCallingModalOpen(user?.manualcall ? true : false));
          dispatch(setCallDialSec(null));
          dispatch(setCurrentCallDetail(null));
          dispatch(setSidebarType('main'));
          dispatch(setCallEnd(false));
          handleTimeReset();
          dispatch(onSetCallStatus(null));
          dispatch(onSetCallType(null));
          if (location.pathname !== '/chat') {
            navigate('/dashboard');
          }
          setIsLoading(false);
          dispatch(setHoldTime(0));
          dispatch(setHoldStartTime(null));
          setFollowUp({
            date_and_time: startOfDay(today),
            isFollowUp: 'no'
          });
          setIsPauseMode(false);
        }, 500);

        // const removeTimer = setTimeout(() => {
        //   dispatch(removeCrmDetails());
        // }, 600);

        return () => {
          clearTimeout(responseTimer);
          // clearTimeout(removeTimer);
        };
      }
    } catch (error) {
      setIsLoading(false);
      const data_message = {
        user_id: user.user_id,
        payload: error?.message,
        message: 'update Crm Information error'
      };
      getLogDetails(data_message);
      console.log('error', error);
    }
  };

  updateCrmRef.current = updateCrm;

  // When embedded in an iframe, ending a call must ONLY hang up the call — the host
  // app owns the CRM disposition/update, so we never run updateCrm here. We also do
  // NOT reset local state at this point: the backend emits a `call_hangup` socket
  // event once the call is actually torn down, and that is what resets everything.
  const onIframeHangup = async () => {
    try {
      if (callId) {
        await dispatch(hangupCall({ call_uuid: callId })).unwrap();
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (!isEmbedded) return;
    if (!callEnd) return;
    onIframeHangup();
  }, [callEnd]);

  // The backend emits `call_hangup` once the call is fully torn down (whether the
  // agent or the remote party ended it). That is our single cue to reset all
  // calling state and return to the dashboard.
  useEffect(() => {
    if (!isEmbedded) return;

    const handleCallHangup = () => {
      console.log('call_hangup socket');
      setWrapsec(0);
      dispatch(setCallId(null));
      dispatch(setCurrentCallDetail(null));
      dispatch(setCallingModalOpen(user?.manualcall ? true : false));
      dispatch(setCallDialSec(null));
      dispatch(setSidebarType('main'));
      dispatch(setCallEnd(false));
      dispatch(onSetCallStatus(null));
      dispatch(onSetCallType(null));
      dispatch(setHoldTime(0));
      dispatch(setHoldStartTime(null));
      dispatch(setScreenType(''));
      handleTimeReset();
      incomingInvitationRef.current = null;
      navigate('/dashboard');
    };

    socket.on('call_hangup', handleCallHangup);
    return () => {
      socket.off('call_hangup', handleCallHangup);
    };
  }, [isEmbedded, user]);
  useEffect(() => {
    if (callStatus === 'WRAPUP' || !callStatus) {
      dispatch(setScreenType(''));
      setNumber('');
      dispatch(setCallNumber(''));
      setWrapsec(0);
    }
  }, [callStatus, breakDetails]);

  useEffect(() => {
    if (isMini) return;
    socket.emit('identify', { user_id: user.user_id, type: user?.type });

    const handleServerRestart = ({ message }) => {
      console.log('server_restart-------->', message);
      socket.emit('identify', { user_id: user.user_id, type: user?.type });
    };

    const handleMessage = ({ message }) => {
      console.log('socket msg-->', message);
      console.log('socket id-->', socket.id);
      const data_message = {
        user_id: user.user_id,
        message: 'socket msg',
        'socket.id': socket.id,
        message_data: message
      };
      getLogDetails(data_message);
      if (!!breakDetails || (sipStatus === 'Un-Register' && Number(user.web_phone) === 0)) {
        return false;
      }
      if ((!!breakDetails || callStatus) && message?.event_type === 'EXTENSION_RINGING') {
        return false;
      }
      dispatch(onSetCallType(message?.call_type));
      if (message?.call_type === 'HANGUP') {
        dispatch(setCallId(message?.origination_uuid));
        dispatch(setCurrentCallDetail(message));
      }
      const userProcessType = user?.process_details?.type;
      const isIncomingManualCall =
        userProcessType === 'InboundAndManual' ||
        userProcessType === 'BlendedAndPredictive' ||
        userProcessType === 'BlendedAndPreview' ||
        userProcessType === 'BlendedAndProgressive';
      if (
        process.env.REACT_APP_INCOMING_SCREEN === 'true' &&
        isIncomingManualCall &&
        message?.call_type !== 'incoming' &&
        message?.event_type === 'EXTENSION_RINGING'
      ) {
        console.log('Manual Call Answer....');
        answerCall();
      }
      if (
  (
    message?.call_type === 'predictive' ||
    message?.call_type === 'incoming' ||
    message?.call_type === 'voicebroadcast' ||
    (message?.call_type === 'manual' &&
            window.self !== window.top &&
            message?.event_type !== 'ANSWER')) &&

        (message?.event_type === 'ANSWER' || message?.event_type === 'EXTENSION_RINGING')
  ) { 
	if (process.env.REACT_APP_INCOMING_SCREEN !== 'true') {
          playBeep();
        }
        dispatch(setCallId(message?.origination_uuid));
        dispatch(setCallingModalOpen(true));
        dispatch(setCurrentCallDetail(message));
        getCrmDetails(message);
        dispatch(setCallDialSec(0));
        dispatch(
          setScreenType(
            message?.call_type === 'incoming' &&
              message?.event_type === 'EXTENSION_RINGING' &&
              process.env.REACT_APP_INCOMING_SCREEN === 'true'
              ? 'INCOMING'
              : 'LIVE_CALL'
          )
        );
        if (
          (message?.call_type === 'incoming' &&
            message?.event_type === 'EXTENSION_RINGING' &&
            process.env.REACT_APP_INCOMING_SCREEN === 'true') ||
          (window.self !== window.top && message?.event_type === 'EXTENSION_RINGING')
        ) {
          console.log('message?.destination_number', message?.destination_number);
          setNumber(message?.destination_number);
          dispatch(setCallNumber(message?.destination_number));
        }

      }
      const callType =
        message?.event_type === 'EXTENSION_RINGING'
          ? message?.call_type === 'predictive'
            ? 'ON CALL'
            : 'RINGING'
          : message?.event_type === 'DIALING'
            ? 'DIALING'
            : message?.event_type === 'ANSWER'
              ? 'ON CALL'
              : message?.event_type === 'HANGUP'
                ? 'WRAPUP'
                : null;
      if (callStatus !== callType) {
        handleTimeReset();
      }
      dispatch(onSetCallStatus(callType));
      if (callType) {
        dispatch(setSelectBreakType(null));
      }
    };

    socket.on('connect', () => {
      console.log('Socket connected: ', socket.id);
      socket.emit('identify', { user_id: user.user_id, type: user?.type });
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log('Socket reconnected after attempt ', attemptNumber);
      socket.emit('identify', { user_id: user.user_id, type: user?.type });
    });

    socket.on('server_restart', handleServerRestart);
    socket.on('sendMessage', handleMessage);

    return () => {
      socket.off('server_restart', handleServerRestart);
      socket.off('sendMessage', handleMessage);
      socket.off('connect');
      socket.off('reconnect');
    };
  }, [user, callStatus, breakDetails, sipStatus, isMini]);

  const getLogDetails = async (data) => {
    try {
      await dispatch(crmInfoLog(data)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const onProgressiveCall = async () => {
    try {
      const response = await dispatch(progressiveCall()).unwrap();
      if (response && response?.data) {
        dispatch(setCallId(response?.data?.origination_uuid));
        dispatch(setCurrentCallDetail(response?.data));
        getCrmDetails(response?.data);
        dispatch(setScreenType('LIVE_CALL'));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isMini) return;
    if (
      user &&
      user?.process_details &&
      (user?.process_details?.type === 'OutboundAndProgressive' ||
        user?.process_details?.type === 'BlendedAndProgressive') &&
      !callStatus &&
      !breakType
    ) {
      const apiRefresh = Number(user?.process_details?.ProgressiveDialingSec) || 5;

      const shouldRegister = Number(user.web_phone) === 0 ? sipStatus === 'Register' : true;

      if (shouldRegister) {
        if (!intervalRef.current) {
          intervalRef.current = setInterval(() => {
            onProgressiveCall();
          }, apiRefresh * 1000);
        }
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user, callStatus, breakType, sipStatus, isMini]);

  const manualDialingOn = async () => {
    try {
      await dispatch(manualDialing()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (breakType && breakType.name === 'OUTGOING') {
      manualDialingOn();
    }
  }, [breakType]);

  const getBreaksList = async () => {
    try {
      const payload = { process_id: user?.process_details?.process_id };
      await dispatch(breaksListGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    getBreaksList();
  }, [user]);

  const onUnregisterCall = async () => {
    try {
      await dispatch(setUnregisterCall()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const onSelectBreak = async (data, type) => {
    try {
      if (type === 'in') {
        await onUnregisterCall();
      }
      const payload = {
        break_id: data?.id,
        break_name: data?.name,
        type: type
      };
      window.__SIP_MANUAL_LOGOUT__ = true;
      reconnectInProgressRef.current = true;
      await dispatch(selectBreak(payload)).unwrap();
      dispatch(setBreakDetails(type === 'in' ? payload : null));
      dispatch(
        setSelectBreakType(
          type === 'in' ? { id: data?.id, name: data?.name, color: 'warning' } : null
        )
      );
      handleTimeReset();
      if (pauseMode === 0) {
        onPauseModeChange(1);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isDialNumber) {
      dialNumber(isDialNumber);
    }
  }, [isDialNumber]);

  const dialNumber = async (number) => {
    setNumber(number);
    dispatch(setCallNumber(number));
    dispatch(setIsReDial(false));
    dispatch(setIsDialNumber(null));
    try {
      const payload = {
        destination_number: number
      };
      const response = await dispatch(manualCall(payload)).unwrap();
      if (response && response?.data) {
        dispatch(setCallDialSec(0));
        dispatch(setCallId(response?.data?.origination_uuid));
        dispatch(setCurrentCallDetail(response?.data));
        // When embedded in an iframe, show the destination number returned by the
        // manual call response in the live call display.
        if (window.self !== window.top && response?.data?.destination_number) {
          setNumber(response.data.destination_number);
          dispatch(setCallNumber(response.data.destination_number));
        }
        getCrmDetails(response?.data);
        dispatch(setScreenType('LIVE_CALL'));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const getCrmDetails = async (data) => {
    try {
      const payload = {
        crm_id: data?.crm_id,
        unique_id: data?.unique_id
      };
      const response = await dispatch(crmInformation(payload)).unwrap();
      const data_message = {
        user_id: user.user_id,
        message: 'try to fetch CRM',
        'socket.id': socket.id,
        message_data: response
      };
      getLogDetails(data_message);
      if (response) {
        dispatch(setSidebarType('CRM'));
        // Inside an iframe the host app renders the CRM, so we show a lightweight
        // "call in progress" placeholder instead of our (blank) CRM screen.
        navigate(isEmbedded ? '/call-in-progress' : '/crm');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (!crmDetails && CurrentCallDetail && CurrentCallDetail?.unique_id) {
      getCrmDetails(CurrentCallDetail);
    }
  }, [crmDetails, callId]);

  const onIdleSet = async () => {
    try {
      await dispatch(setIdleCall()).unwrap();
      setWrapsec(0);
      dispatch(setCallId(null));
      dispatch(setCurrentCallDetail(null));
      dispatch(setCallingModalOpen(user?.manualcall ? true : false));
      dispatch(setCallDialSec(null));
      dispatch(setSidebarType('main'));
      dispatch(setCallEnd(false));
      handleTimeReset();
      dispatch(onSetCallStatus(null));
      dispatch(onSetCallType(null));
      dispatch(setHoldTime(0));
      dispatch(setHoldStartTime(null));
      incomingInvitationRef.current = null;
      if (location.pathname !== '/chat') {
        navigate('/dashboard');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (callStatus === 'WRAPUP' && wrapsec > 60 && !isEmbedded) {
      onIdleSet();
    }
  }, [callStatus, wrapsec, isEmbedded]);

  const renderScreen = () => {
    switch (screenType) {
      case 'LIVE_CALL':
        return <DefaultLiveCall setNumber={setNumber} number={number} />;

      case 'INCOMING':
        return (
          <IncomingCallScreen
            setNumber={setNumber}
            number={number}
            answerCall={answerCall}
            stopRingtone={stopRingtone}
            incomingInvitationRef={incomingInvitationRef}
          />
        );

      case 'DTMF':
        return <DtmfScreen />;

      case 'TRANSFER':
        return <TransferScreen number={number} />;

      case 'AGENT_TRANSFER':
        return <AgentTransferScreen number={number} />;

      case 'ATTENDED_TRANSFER':
        return <UserTransferScreen number={number} />;

      case 'BLIND_TRANSFER':
        return <UserTransferScreen number={number} />;

      case 'TRANSFER_HANGUP':
        return <TransferHangupScreen number={number} />;

      case 'CONFERENCE':
        return <ConferenceScreen number={number} />;

      case 'CONFERENCE_LIVE':
        return <ConferenceLiveScreen number={number} setNumber={setNumber} />;

      default:
        return callStatus === 'WRAPUP' ? (
          <div className="input-container">
            <div className="d-flex flex-column gap-2 justify-content-center align-items-center">
              <div
                className="bg-violet rounded-circle d-flex justify-content-center align-items-center cursor-pointer"
                style={{ height: '70px', width: '70px' }}
                onClick={() => {
                  dispatch(setIsReDial(true));
                  socket.emit('identify-redial', { user_id: user.user_id, type: user?.type });
                  dispatch(setCallEnd(true));
                  setNumber('');
                  dispatch(setCallNumber(''));
                  dispatch(setScreenType(null));
                }}>
                <Icon.PhoneForwarded className="text-white" size={30} />
              </div>
              <div className="text-white fs-5 fw-bold">{t('redial')}</div>
            </div>
          </div>
        ) : (
          <DialPadScreen
            number={number}
            setNumber={setNumber}
            setDuration={handleTimeReset}
            duration={duration}
            onIdleSet={onIdleSet}
          />
        );
    }
  };

  const renderCallStatus = () => {
    const callStatusClasses = {
      WRAPUP: 'bg-violet',
      RINGING: 'bg-blue',
      DIALING: 'bg-purple',
      'ON CALL': 'bg-green'
    };

    const statusClass =
      callStatusClasses[callStatus] || (breakType?.color ? `bg-${breakType.color}` : 'bg-info');
    return (
      <div className={`${statusClass} py-1 w-100 text-center text-white rounded-2 idle-time`}>
        <span className="text-uppercase">
          {callStatus
            ? t(callStatus)
            : (breakType?.name && breakType?.name === 'OUTGOING'
              ? t('OUTGOING')
              : breakType?.name) || t('IDLE')}
        </span>{' '}
        <span>
          <Timer duration={duration} setDuration={setDuration} />
        </span>
      </div>
    );
  };

  const renderBreakStatus = () => {
    if (callStatus && callStatus !== 'WRAPUP') {
      return (
        <div className={`px-4 break-container ${!user?.manualcall && !callId && 'pt-0'}`}>
          <Button
            caret
            size="sm"
            className="ready-btn w-100"
            onClick={() => {
              getCrmDetails(CurrentCallDetail);
            }}>
            {t('refetchDetails')}
          </Button>
        </div>
      );
    }
    if (callStatus === 'WRAPUP') {
      return (
        <div className={`px-4 break-container ${!user?.manualcall && !callId && 'pt-0'}`}>
          <Button
            caret
            size="sm"
            className="ready-btn w-100"
            disabled={location.pathname === '/crm'}
            onClick={() => {
              onIdleSet();
            }}>
            {t('setIdle')}
          </Button>
        </div>
      );
    }
    if ((breakType && breakType?.name === 'OUTGOING') || callStatus !== null) {
      return false;
    }
    return (
      <div className={`px-4 break-container ${!user?.manualcall && !callId && 'pt-0'}`}>
        {breakType ? (
          <Button
            caret
            size="sm"
            className="ready-btn w-100"
            onClick={() => {
              onSelectBreak(breakType, 'out');
            }}>
            {t('ready')}
          </Button>
        ) : (
          <ButtonDropdown
            className="break-button-dropdown"
            isOpen={isOpen}
            toggle={() => setIsOpen(!isOpen)}>
            <DropdownToggle className="break-button" caret size="sm">
              {t('break')}
            </DropdownToggle>
            <DropdownMenu>
              {breaksList.map((val, index) => {
                return (
                  <DropdownItem
                    key={index}
                    onClick={() => {
                      onSelectBreak(val, 'in');
                    }}>
                    {val?.name}
                  </DropdownItem>
                );
              })}
            </DropdownMenu>
          </ButtonDropdown>
        )}
      </div>
    );
  };

  const renderProcessDropdown = () => {
    return user?.multi_process_data?.length > 0 ? (
      <div className="px-4">
        <ButtonDropdown
          className="break-button-dropdown"
          isOpen={isConVoxOpen}
          toggle={() => setIsConVoxOpen((prev) => !prev)}>
          <DropdownToggle className="break-button" caret size="sm">
            {user?.process_details?.name}
          </DropdownToggle>

          <DropdownMenu>
            {user.multi_process_data.map((process) => (
              <DropdownItem
                key={process.id}
                active={process.id === user?.process_details?.process_id}
                onClick={() => {
                  if (
                    process.id !== user?.process_details?.process_id &&
                    callStatus === null &&
                    !breakType
                  ) {
                    setIsProcessModalOpen({ isOpen: true, selectedProcess: process });
                  }
                }}>
                {process.name}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </ButtonDropdown>
      </div>
    ) : (
      <div className="px-4">
        <ButtonDropdown
          className="break-button-dropdown"
          isOpen={isConVoxOpen}
          toggle={() => setIsConVoxOpen(!isConVoxOpen)}>
          <DropdownToggle className="break-button" caret size="sm">
            {user?.process_details?.name}
          </DropdownToggle>
          <DropdownMenu>{user?.process_details?.name}</DropdownMenu>
        </ButtonDropdown>
      </div>
    );
  };

  return (
    <React.Fragment>
      {!user?.manualcall && !callId ? (
        <div className="predictive-container">
          <div className="pb-2 text-center text-light fs-6">{getCurrentDateTime(timezone)}</div>
          {renderProcessDropdown()}
          <div className="px-4 py-2">{renderCallStatus()}</div>
          {renderBreakStatus()}
        </div>
      ) : (
        <div className="calling-sidebar p-3 position-relative">
          <div
            className="position-absolute bg-primary shadow-sm rounded-5 top-50 border-white text-white d-flex justify-content-center align-items-center callpase-icon"
            onClick={() => dispatch(setCallingModalOpen(false))}>
            <Icon.ChevronRight />
          </div>
          <div className="shadow-sm dail-page-container rounded-4 py-3">
            <div className="pb-2 text-center text-light fs-6">{getCurrentDateTime(timezone)}</div>
            {renderProcessDropdown()}
            <div className="px-4 py-2">{renderCallStatus()}</div>
            {renderScreen()}
            {renderBreakStatus()}
          </div>
        </div>
      )}
      <MicrophonePermissionModal
        isOpen={showMicModal}
        permissionState={micPermission}
        onEnableClick={requestMicrophonePermission}
        onLogout={onLogout}
      />
    </React.Fragment>
  );
};

CallingSidebar.propTypes = {
  setIsProcessModalOpen: PropTypes.func,
  isMini: PropTypes.bool
};

export default CallingSidebar;
